import React, { useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Row } from 'reactstrap';
import { faBorderAll } from '@fortawesome/free-solid-svg-icons';
import { useLocation } from 'react-router';
import getCells from './Cells';
import {
  Button,
  PageHeader,
  Section,
  SpinnerArea,
  Table,
  TablePagination,
  TabBar,
  CustomDropDown,
} from '../../shared/components';
import { findMapped, findMapId, capitalize } from '../../shared/misc/utils';
import useServiceReducer from '../../shared/hooks/useServiceReducer';
import reducer from './reducer';
import Context from '../App/MainProvider/context';

import missionsService from '../../services/missionsService';
import ColumnChecks from '../Operations/ColumnChecks';
import { MissionFilter, MissionItem, QueryGroup } from '../../services/types/Missions';
import Role from '../../types/role';
import BasicRadioGroup from '../../shared/components/BasicRadioGroup';

const Missions: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const queryString = new URLSearchParams(location.search);
  const q = (key: string) => queryString.get(key);
  const operationId = q('operationId') ? +(q('operationId') || 0) : undefined;

  const { service, user } = useContext(Context);

  const initialHiddenColumns = operationId
    ? ['operationNumber', 'operationName', 'destinationCountry', 'country']
    : ['destinationCountry', 'country'];
  const initialFilter: MissionFilter = {
    queryGroup: QueryGroup.All,
    assigned: true,
    pageNumber: 1,
    pageSize: 10,
    sortField: 'StartDate',
    sortDescending: true,
    operationId,
    hiddenColumns: initialHiddenColumns,
  };

  const [data, loading, filter, dispatch] = useServiceReducer(
    missionsService.getMissions,
    reducer,
    initialFilter
  );

  const [excelLoading, setExcelLoading] = useState(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>(initialHiddenColumns);

  useEffect(() => {
    if (filter.queryGroup === QueryGroup.Execution) {
      setHiddenColumns(
        [...hiddenColumns, 'operationEsic', 'operationDccrc'].filter(
          (x, i, s) => s.indexOf(x) === i && x !== 'operationSpr'
        )
      );
    } else if (filter.queryGroup === QueryGroup.Preparation) {
      setHiddenColumns(
        [...hiddenColumns, 'operationSpr'].filter(
          (x, i, s) => s.indexOf(x) === i && x !== 'operationEsic' && x !== 'operationDccrc'
        )
      );
    } else {
      setHiddenColumns(
        [...hiddenColumns, 'operationSpr'].filter(
          (x, i, s) =>
            s.indexOf(x) === i &&
            x !== 'operationEsic' &&
            x !== 'operationDccrc' &&
            x !== 'operationSpr'
        )
      );
    }
  }, [filter.queryGroup, filter.assigned]);

  const queryGroups = [
    { id: 1, text: t('all'), htmlId: 'tabAll', mapped: QueryGroup.All },
    {
      id: 2,
      text: t('inPreparation'),
      htmlId: 'tabPreparation',
      mapped: QueryGroup.Preparation,
    },
    {
      id: 3,
      text: t('inExecution'),
      htmlId: 'tabEligibility',
      mapped: QueryGroup.Execution,
    },
  ];

  const assignedFilterItems = [
    {
      id: 1,
      text: t(user.roleCode === Role.Specialist ? 'myMissions' : 'groupMissions'),
      htmlId: 'assignedFilterMy',
      mapped: true,
    },
    { id: 2, text: t('allMissions'), htmlId: 'assignedFilterAll', mapped: false },
  ];

  const setQueryGroup = (tabId: number) => {
    const queryGroup = findMapped(queryGroups, tabId);
    dispatch({ type: 'QUERY_GROUP', value: queryGroup });
  };

  const setAssigned = (radId: number) => {
    const assigned = findMapped(assignedFilterItems, radId);
    dispatch({ type: 'ASSIGNED', value: assigned });
  };

  const changePage = (page: number) => dispatch({ type: 'PAGE', value: page });

  const changePageSize = (size: number) => dispatch({ type: 'PAGE_SIZE', value: size });

  const setColumnOrder = (sortField: string, sortDescending: boolean) =>
    dispatch({ type: 'ORDER', column: sortField, descending: sortDescending });

  const exportExcel = () => {
    const pascalHiddenColumns = hiddenColumns.map((x) => capitalize(x));
    const excelParam = {
      ...filter,
      pageSize: 0,
      pageNumber: 1,
      hiddenColumns: pascalHiddenColumns,
    };
    service(() => missionsService.exportExcel(excelParam), { loader: setExcelLoading });
  };

  const hideColumns = (id: string, value: boolean) => {
    if (!value) {
      setHiddenColumns([...hiddenColumns, id]);
    } else {
      const columns = [...hiddenColumns].filter((x) => x !== id);
      setHiddenColumns(columns);
    }
  };

  const isOperRole = [Role.Specialist, Role.GroupHead].includes(user.roleCode);

  const title =
    operationId && data && data.list && data.list.length > 0
      ? `${data.list[0].operationNumber} / ${t('missions')}`
      : t('missions');

  return (
    <Container className="dashboard">
      <PageHeader title={title} noBack />
      <Row>
        <Section>
          {!operationId && (
            <div className="header-controls">
              {isOperRole && (
                <BasicRadioGroup
                  name="select"
                  elements={assignedFilterItems}
                  onChange={setAssigned}
                  value={findMapId(assignedFilterItems, filter.assigned)}
                />
              )}
              <TabBar
                id="queryGroupTabBar"
                tabSelected={findMapId(queryGroups, filter.queryGroup)}
                setTabSelected={setQueryGroup}
                headers={queryGroups}
              />
            </div>
          )}
          <div>
            <div className="details-bar">
              <div className="content">
                <h4>
                  {data &&
                    t('showingMissions', { from: data.from, to: data.to, total: data.total })}
                </h4>
              </div>
            </div>
            <div className="button-container-bar">
              <div className="button-container-bar--left" />
              <div className="button-container-bar--right">
                <CustomDropDown text={t('columns')} icon={faBorderAll} className="oper-columns">
                  <ColumnChecks<MissionItem>
                    cells={getCells([])}
                    removeColums={[]}
                    hideColumns={hiddenColumns}
                    setHideColumn={hideColumns}
                  />
                </CustomDropDown>
                <Button
                  htmlId="btnExcel"
                  text={t('exportToExcel')}
                  onClick={exportExcel}
                  icon="file-excel"
                  className="btn-tertiary"
                />
              </div>
            </div>
            <SpinnerArea loading={loading || excelLoading}>
              {data && (
                <>
                  <Table
                    data={data.list}
                    cells={getCells(hiddenColumns)}
                    keyFunc={(x) => x.id}
                    sortData={filter}
                    changeSort={setColumnOrder}
                  />

                  <TablePagination
                    pageSize={filter.pageSize}
                    pageNumber={filter.pageNumber}
                    total={data.total}
                    onChangePage={changePage}
                    onChangeSize={changePageSize}
                  />
                </>
              )}
            </SpinnerArea>
          </div>
        </Section>
      </Row>
    </Container>
  );
};

export default Missions;
